import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Users, Shield, Share2 } from 'lucide-react';
import { MembersTab } from './MembersTab';
import { RolesTab } from './RolesTab';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { useAuthStore } from '@/lib/auth/authStore';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { useCanView } from '@/lib/permissions/useCanAccess';

type TabId = 'members' | 'roles' | 'grants';

export function AccessManagerPage() {
  const app = useAuthStore((s) => s.app);
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewUsers = useCanView('User');
  const canViewRoles = useCanView('Role');
  const canViewGrants = useCanView('ExternalAccessGrant');

  const visibleTabs = useMemo<TabId[]>(() => {
    const tabs: TabId[] = [];
    if (canViewUsers) tabs.push('members');
    if (canViewRoles) tabs.push('roles');
    // 'grants' (External Sharing) is intentionally excluded — coming soon, not selectable.
    return tabs;
  }, [canViewUsers, canViewRoles]);

  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const activeTab: TabId | null =
    tabFromUrl && visibleTabs.includes(tabFromUrl)
      ? tabFromUrl
      : visibleTabs[0] ?? null;

  useFavoritable(
    activeTab
      ? `${t('accessManager.title')} · ${t(`accessManager.tab.${activeTab}`)}`
      : t('accessManager.title'),
  );

  function setTab(next: string) {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    if (next !== 'roles') params.delete('editRole');
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="page space-y-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">{t('accessManager.title')}</h1>
          {app && <Badge tone="accent">{t('accessManager.scope', { app })}</Badge>}
        </div>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('accessManager.subtitle', { app: app ?? t('accessManager.currentApp') })}
        </p>
      </header>

      {activeTab === null ? (
        <EmptyState
          icon={<Shield className="h-6 w-6" />}
          title={t('accessManager.noAccess.title')}
          description={t('accessManager.noAccess.body')}
        />
      ) : (
        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList>
            {canViewUsers && (
              <TabsTrigger value="members" className="gap-2">
                <Users className="h-4 w-4" /> {t('accessManager.tab.members')}
              </TabsTrigger>
            )}
            {canViewRoles && (
              <TabsTrigger value="roles" className="gap-2">
                <Shield className="h-4 w-4" /> {t('accessManager.tab.roles')}
              </TabsTrigger>
            )}
            {canViewGrants && (
              <TabsTrigger
                value="grants"
                disabled
                aria-disabled="true"
                title={t('common.comingSoon')}
                className="gap-2 cursor-not-allowed opacity-60 hover:text-[var(--color-fg-muted)]"
              >
                <Share2 className="h-4 w-4" /> {t('accessManager.tab.grants')}
                <Badge tone="warn" className="ml-1">{t('common.comingSoon')}</Badge>
              </TabsTrigger>
            )}
          </TabsList>

          {canViewUsers && (
            <TabsContent value="members">
              <MembersTab />
            </TabsContent>
          )}
          {canViewRoles && (
            <TabsContent value="roles">
              <RolesTab />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}
